import { Schema, Effect } from "effect";
import { describeIntRange } from "../openapi-query.js";
import { JsonUnknown } from "../json-unknown.js";
import { HttpApiEndpoint, HttpApiError, HttpApiGroup, HttpApiSchema } from "effect/http-api";
import {
  CareerDomainLookupRequestSchema,
  CareerDomainLookupResponseSchema,
} from "./domain-lookup.js";
import { JobAdStatusesRequestSchema, JobAdStatusesResponseSchema } from "./job-ad-status.js";
import {
  HiringTrendsQuery,
  HiringTrendsResponse,
  HiringTrendCompaniesQuery,
  HiringTrendCompaniesResponse,
  HiringTrendAdsQuery,
  HiringTrendAdsResponse,
} from "./hiring-trends.js";
import { UUID } from "../uuid.js";

export * from "./domain-lookup.js";
export * from "./job-ad-status.js";
export * from "./hiring-trends.js";

const NullableTimestamp = Schema.NullOr(Schema.String);

export const CareerPositionSchema = Schema.Struct({
  orgnr: Schema.NullOr(Schema.String),
  sourceKey: Schema.String,
  sourceUrl: Schema.NullOr(Schema.String),
  applyUrl: Schema.NullOr(Schema.String),
  title: Schema.NullOr(Schema.String),
  organizationName: Schema.NullOr(Schema.String),
  location: Schema.NullOr(Schema.String),
  employmentType: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  workMode: Schema.NullOr(Schema.String),
  seniority: Schema.NullOr(Schema.String),
  department: Schema.NullOr(Schema.String),
  salaryText: Schema.NullOr(Schema.String),
  publishedAt: Schema.NullOr(Schema.String),
  deadlineAt: Schema.NullOr(Schema.String),
  tools: Schema.Array(Schema.String),
  language: Schema.NullOr(Schema.String),
  confidence: Schema.NullOr(Schema.Number),
  evidence: JsonUnknown,
  firstSeenAt: Schema.String,
  lastSeenAt: Schema.String,
  closedAt: NullableTimestamp,
});

export const CareerPageSchema = Schema.Struct({
  careerPageId: Schema.String,
  canonicalUrl: Schema.String,
  discoveryStatus: Schema.String,
  firstSeenAt: Schema.String,
  lastSeenAt: Schema.String,
  lastCheckedAt: NullableTimestamp,
  nextDueAt: NullableTimestamp,
  lastCrawlStatus: Schema.NullOr(Schema.String),
  lastCrawlCompleteness: Schema.NullOr(Schema.String),
  lastCrawlAt: NullableTimestamp,
  positions: Schema.Array(CareerPositionSchema),
});

export const DomainCareersResponseSchema = Schema.Struct({
  domain: Schema.String,
  pages: Schema.Array(CareerPageSchema),
});

export const CareerAdminDomainSchema = Schema.Struct({
  domain: Schema.String,
  paused: Schema.Boolean,
  pauseReason: Schema.NullOr(Schema.String),
  seedUrl: Schema.NullOr(Schema.String),
  nextDueAt: NullableTimestamp,
  lastAttemptAt: NullableTimestamp,
  lastStatus: Schema.NullOr(Schema.String),
  lastOutcomeCode: Schema.NullOr(Schema.String),
  lastMessage: Schema.NullOr(Schema.String),
  lastHttpStatus: Schema.NullOr(Schema.Number),
  lastDetails: JsonUnknown,
  attemptCount: Schema.Number,
  inputTokens: Schema.NullOr(Schema.Number),
  outputTokens: Schema.NullOr(Schema.Number),
  reasoningTokens: Schema.NullOr(Schema.Number),
  modelName: Schema.NullOr(Schema.String),
});

export const CareerAdminDomainsResponseSchema = Schema.Struct({
  canManage: Schema.Boolean,
  items: Schema.Array(CareerAdminDomainSchema),
});

export const CareerActivationSharedCompanySchema = Schema.Struct({
  orgnr: Schema.String,
  verified: Schema.Boolean,
  name: Schema.OptionFromOptional(Schema.String),
});

export const CompanyDomainManualVerificationSchema = Schema.Struct({
  verifiedAt: Schema.String,
  verifiedByUserId: Schema.String,
});

export const CareerActivationDomainSchema = Schema.Struct({
  domain: Schema.String,
  associationVerified: Schema.Boolean,
  enabled: Schema.Boolean,
  paused: Schema.Boolean,
  eligible: Schema.Boolean,
  blockedReason: Schema.NullOr(Schema.String),
  sharedCompanies: Schema.Array(CareerActivationSharedCompanySchema),
  /** The latest human link or verification of this company-domain pair, when one was recorded. */
  manualVerification: Schema.OptionFromOptional(CompanyDomainManualVerificationSchema),
});

export const CareerActivationResponseSchema = Schema.Struct({
  globalEnabled: Schema.Boolean,
  domains: Schema.Array(CareerActivationDomainSchema),
});

export const CareerActivationRequestSchema = Schema.Struct({
  domain: Schema.String,
  enabled: Schema.Boolean,
});

export const CareerDomainControlRequestSchema = Schema.Struct({
  paused: Schema.Boolean,
  reason: Schema.NullOr(Schema.String),
});

export const CareerSeedRequestSchema = Schema.Struct({ seedUrl: Schema.String });

export const CompanyDomainLinkStateSchema = Schema.Literals([
  "none",
  "unverified",
  "verified",
  "removed",
]);

export const CompanyDomainPreviewQuerySchema = Schema.Struct({ domain: Schema.String });

export const CompanyDomainPreviewResponseSchema = Schema.Struct({
  /** The normalized bare domain that a link request would store. */
  domain: Schema.String,
  state: CompanyDomainLinkStateSchema,
  /** Live links from other companies, verified first, capped at `otherCompanyLimit`. */
  otherCompanies: Schema.Array(CareerActivationSharedCompanySchema),
  otherCompanyCount: Schema.Number,
  otherCompanyLimit: Schema.Number,
  /** Live links counted by the career-eligibility aggregator guard after this company is linked. */
  mappedCompanyCount: Schema.Number,
  mappedCompanyLimit: Schema.Number,
});

export const LinkCompanyDomainRequestSchema = Schema.Struct({ domain: Schema.String });

export const CompanyDomainVerificationRequestSchema = Schema.Struct({ verified: Schema.Boolean });

export const CompanyDomainParamsSchema = Schema.Struct({
  orgnr: Schema.String,
  domain: Schema.String,
});

export const CompanyDomainInvalidError = Schema.Struct({
  reason: Schema.Literal("invalid_domain"),
}).pipe(HttpApiSchema.status(400));

export const CompanyDomainNotFoundError = Schema.Struct({
  reason: Schema.Literals(["company_not_found", "link_not_found"]),
}).pipe(HttpApiSchema.status(404));

export type CompanyDomainPreviewResponse = Schema.Schema.Type<
  typeof CompanyDomainPreviewResponseSchema
>;
export type CareerActivationResponse = Schema.Schema.Type<typeof CareerActivationResponseSchema>;

const CompanyAdsCareerFilterSchema = Schema.Struct({
  roles: Schema.OptionFromOptional(
    Schema.Array(Schema.NonEmptyString.pipe(Schema.check(Schema.isPattern(/\S/u)))).pipe(
      Schema.check(Schema.isMinLength(1)),
      Schema.check(Schema.isMaxLength(20))
    )
  ),
  keywords: Schema.OptionFromOptional(
    Schema.Array(Schema.NonEmptyString.pipe(Schema.check(Schema.isPattern(/\S/u)))).pipe(
      Schema.check(Schema.isMinLength(1)),
      Schema.check(Schema.isMaxLength(20))
    )
  ),
  scope: Schema.OptionFromOptional(Schema.Literals(["company", "group", "any"])),
  minAdCount: Schema.OptionFromOptional(Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0)))),
  minDensity: Schema.OptionFromOptional(
    Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1 })))
  ),
}).pipe(
  Schema.check(
    Schema.makeFilter(
      (filter) => filter.minDensity._tag === "None" || filter.keywords._tag === "Some",
      {
        message: "minDensity requires at least one keyword",
      }
    )
  )
);

export const CompanyAdsPaginationRequestSchema = Schema.Struct({
  companyId: Schema.NonEmptyString,
  source: Schema.Literals(["career", "jobtech"]),
  careerAds: Schema.OptionFromOptional(CompanyAdsCareerFilterSchema),
  activeJobTitles: Schema.OptionFromOptional(Schema.Array(Schema.NonEmptyString)),
  offset: Schema.OptionFromOptional(
    Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))
  ),
  limit: Schema.OptionFromOptional(
    Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 100 })))
  ),
}).pipe(
  Schema.check(
    Schema.makeFilter(
      (request) =>
        !(
          (request.source === "career" && request.activeJobTitles._tag === "Some") ||
          (request.source === "jobtech" && request.careerAds._tag === "Some")
        ),
      { message: "Filters must match the requested ad source" }
    )
  )
);

export const CompanyAdsPaginationResponseSchema = Schema.Struct({
  items: Schema.Array(
    Schema.Struct({
      source: Schema.Literals(["career", "jobtech"]),
      adId: Schema.String,
      title: Schema.OptionFromOptional(Schema.String),
      sourceUrl: Schema.OptionFromOptional(Schema.String),
      applyUrl: Schema.OptionFromOptional(Schema.String),
      location: Schema.OptionFromOptional(Schema.String),
      deadline: Schema.OptionFromOptional(Schema.String),
      firstSeenAt: Schema.OptionFromOptional(Schema.String),
      lastSeenAt: Schema.OptionFromOptional(Schema.String),
      matchedKeywords: Schema.Array(Schema.String),
      provenance: Schema.Struct({
        scope: Schema.Literals(["company", "group"]),
        companyId: Schema.String,
        sourceOrgnr: Schema.String,
        domain: Schema.OptionFromOptional(Schema.String),
      }),
    })
  ).pipe(Schema.check(Schema.isMaxLength(100))),
  page: Schema.Struct({
    offset: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
    limit: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 100 }))),
    totalMatchingAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
    returnedCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
    hasMore: Schema.Boolean,
    nextOffset: Schema.OptionFromOptional(
      Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))
    ),
    cohortRevision: Schema.String,
    selectedScope: Schema.OptionFromOptional(Schema.Literals(["company", "group"])),
  }),
  coverageWarnings: Schema.Array(Schema.String),
  asOf: Schema.OptionFromOptional(Schema.String),
});

const HiringQuality = Schema.Literals(["exact", "lower_bound", "unknown"]);
const HiringAbsenceReason = Schema.Literals([
  "missing_baseline",
  "baseline_zero",
  "stale_snapshot",
  "future_snapshot",
  "incomplete_snapshot",
  "mapping_changed",
  "scope_changed",
  "ambiguous_domain",
  "ambiguous_identity",
  "latest_attempt_incomplete",
  "no_confirmed_pages",
  "discovery_incomplete",
  "page_incomplete",
  "page_failed",
  "page_deferred",
  "page_cancelled",
  "legacy_worker_missing_evidence",
  "no_observation",
]);

const HiringFreshness = Schema.Literals(["fresh", "stale", "unknown"]);
const HiringSignalScope = Schema.Literals(["company", "group"]);

/** The immutable career observation and coverage context used for company hiring totals. */
export const CompanyHiringSnapshotSchema = Schema.Struct({
  /** Defaults to company for records created before hiring-signal scoping was introduced. */
  scope: Schema.optional(HiringSignalScope),
  snapshotId: Schema.optional(UUID),
  asOf: Schema.optional(Schema.String),
  observedCount: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
  quality: HiringQuality,
  freshness: HiringFreshness,
  reason: Schema.optional(HiringAbsenceReason),
  observationWindow: Schema.optional(Schema.Struct({ start: Schema.String, end: Schema.String })),
  sourceUrls: Schema.Array(Schema.String),
});

/** A signed change from the current observation to a dated comparable baseline. Ratios stay raw (0.2 = 20%). */
export const CompanyHiringDeltaSchema = Schema.Struct({
  count: Schema.optional(Schema.Int),
  ratio: Schema.optional(Schema.Number),
  baselineSnapshotId: Schema.optional(UUID),
  baselineAsOf: Schema.optional(Schema.String),
  reason: Schema.optional(HiringAbsenceReason),
});

/** One dated point in company hiring history; absent counts represent gaps, never zero. */
export const CompanyHiringHistoryItemSchema = Schema.Struct({
  /** Defaults to company for records created before hiring-signal scoping was introduced. */
  scope: Schema.optional(HiringSignalScope),
  snapshotId: UUID,
  asOf: Schema.String,
  observedCount: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
  quality: HiringQuality,
  reason: Schema.optional(HiringAbsenceReason),
});

/** Latest supported job details for a member of the requested immutable snapshot. */
export const CompanyHiringJobSchema = Schema.Struct({
  jobId: Schema.String,
  title: Schema.optional(Schema.String),
  sourceUrl: Schema.optional(Schema.String),
  applyUrl: Schema.optional(Schema.String),
  location: Schema.optional(Schema.String),
  department: Schema.optional(Schema.String),
  employmentType: Schema.optional(Schema.String),
  publishedAt: Schema.optional(Schema.String),
  firstSeenAt: Schema.String,
  lastSeenAt: Schema.String,
  identityQuality: Schema.Literals(["canonical_detail_url", "listing_only"]),
  textAsOf: Schema.optional(Schema.String),
});

export const CompanyHiringJobsPageSchema = Schema.Struct({
  snapshotId: UUID,
  items: Schema.Array(CompanyHiringJobSchema),
  offset: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  limit: Schema.Int.pipe(
    Schema.check(Schema.isGreaterThan(0)),
    Schema.check(Schema.isLessThanOrEqualTo(100))
  ),
  total: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  nextOffset: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
});

/** Count and share of analyzed ads that list each curated recruitment keyword. */
export const CompanyHiringKeywordSummarySchema = Schema.Struct({
  snapshotId: UUID,
  /** Defaults to 1 for legacy summaries. */
  revision: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0)))),
  /** Legacy summaries expose structured tools; newer extraction may cover broader keywords. */
  sourceKind: Schema.optional(Schema.Literals(["tools", "keywords"])),
  /** Present only for AI-extracted keyword summaries. */
  extractionVersion: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0)))),
  computedAt: Schema.optional(Schema.String),
  totalAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  analyzedAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  items: Schema.Array(
    Schema.Struct({
      keyword: Schema.String,
      adCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
      /** Fraction of analyzed ads; 0.2 means 20%. */
      density: Schema.Number.pipe(
        Schema.check(Schema.isGreaterThan(0)),
        Schema.check(Schema.isLessThanOrEqualTo(1))
      ),
    })
  ),
});

/** A current open ad, annotated with the confirmed domain where it was observed. */
export const CurrentOpenCareerAdSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.NullOr(Schema.String),
  location: Schema.NullOr(Schema.String),
  sourceUrl: Schema.NullOr(Schema.String),
  applyUrl: Schema.NullOr(Schema.String),
  firstSeenAt: Schema.String,
  lastSeenAt: Schema.String,
  sourceDomain: Schema.String,
});

export const CurrentOpenCareerAdsPageSchema = Schema.Struct({
  items: Schema.Array(CurrentOpenCareerAdSchema),
  offset: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  limit: Schema.Int.pipe(
    Schema.check(Schema.isGreaterThan(0)),
    Schema.check(Schema.isLessThanOrEqualTo(100))
  ),
  total: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  nextOffset: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
  /** Changes when current cohort membership or last-seen ordering changes between pages. */
  cohortRevision: Schema.optional(Schema.String),
});

/** Curated keyword summary across current ads, without an immutable snapshot revision. */
export const CurrentOpenCareerKeywordSummarySchema = Schema.Struct({
  sourceKind: Schema.Literal("keywords"),
  totalAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  analyzedAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  items: Schema.Array(
    Schema.Struct({
      keyword: Schema.String,
      adCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
      /** Fraction of analyzed ads; 0.2 means 20%. */
      density: Schema.Number.pipe(
        Schema.check(Schema.isGreaterThan(0)),
        Schema.check(Schema.isLessThanOrEqualTo(1))
      ),
    })
  ),
});

export const CurrentOpenCareerCollectionSchema = Schema.Struct({
  coverage: Schema.Literals(["complete", "partial", "unknown"]),
  warnings: Schema.Array(Schema.String),
});

export const CurrentOpenCareerSignalSchema = Schema.Struct({
  /** Null only when the scope is surfaced as unknown due to ambiguous source attribution. */
  sourceOrgnr: Schema.NullOr(Schema.String),
  ads: CurrentOpenCareerAdsPageSchema,
  keywords: CurrentOpenCareerKeywordSummarySchema,
  collection: CurrentOpenCareerCollectionSchema,
});

export const CurrentOpenCareerSchema = Schema.Struct({
  company: Schema.optional(CurrentOpenCareerSignalSchema),
  group: Schema.optional(CurrentOpenCareerSignalSchema),
});

const CompanyHiringDeltasSchema = Schema.Struct({
  previous: Schema.optional(CompanyHiringDeltaSchema),
  "1m": Schema.optional(CompanyHiringDeltaSchema),
  "3m": Schema.optional(CompanyHiringDeltaSchema),
  "6m": Schema.optional(CompanyHiringDeltaSchema),
  "1y": Schema.optional(CompanyHiringDeltaSchema),
});

const CompanyHiringLatestAttemptSchema = Schema.Struct({
  attemptedAt: Schema.String,
  status: Schema.Literals(["collecting", "complete", "partial", "failed"]),
  reason: Schema.optional(HiringAbsenceReason),
});

/** A separate shared group signal for a member company with its own hiring signal. */
export const CompanyGroupHiringSignalSchema = Schema.Struct({
  representativeOrgnr: Schema.String,
  current: CompanyHiringSnapshotSchema,
  selectedSnapshot: Schema.optional(CompanyHiringSnapshotSchema),
  latestAttempt: Schema.optional(CompanyHiringLatestAttemptSchema),
  deltas: CompanyHiringDeltasSchema,
  history: Schema.Array(CompanyHiringHistoryItemSchema).pipe(Schema.check(Schema.isMaxLength(60))),
  jobs: Schema.optional(CompanyHiringJobsPageSchema),
  keywords: Schema.optional(CompanyHiringKeywordSummarySchema),
});

/** Read-only company hiring summary, dated history, and jobs from one snapshot. */
export const CompanyHiringResponseSchema = Schema.Struct({
  orgnr: Schema.String,
  source: Schema.Literal("company_careers"),
  /** Guarded current projection; count may be omitted when evidence is stale or incomplete. */
  current: Schema.optional(CompanyHiringSnapshotSchema),
  /** Selected immutable snapshot for history/job pagination, pinned by snapshotId when supplied. */
  selectedSnapshot: Schema.optional(CompanyHiringSnapshotSchema),
  latestAttempt: Schema.optional(CompanyHiringLatestAttemptSchema),
  deltas: CompanyHiringDeltasSchema,
  history: Schema.Array(CompanyHiringHistoryItemSchema).pipe(Schema.check(Schema.isMaxLength(60))),
  jobs: Schema.optional(CompanyHiringJobsPageSchema),
  keywords: Schema.optional(CompanyHiringKeywordSummarySchema),
  groupSignal: Schema.optional(CompanyGroupHiringSignalSchema),
  /** Live open-ad evidence; immutable observation fields above remain unchanged. */
  currentOpen: Schema.optional(CurrentOpenCareerSchema),
});

export type CompanyHiringResponse = Schema.Schema.Type<typeof CompanyHiringResponseSchema>;
export type CompanyHiringSnapshot = Schema.Schema.Type<typeof CompanyHiringSnapshotSchema>;
export type CompanyHiringDelta = Schema.Schema.Type<typeof CompanyHiringDeltaSchema>;
export type CompanyHiringHistoryItem = Schema.Schema.Type<typeof CompanyHiringHistoryItemSchema>;
export type CompanyHiringJobsPage = Schema.Schema.Type<typeof CompanyHiringJobsPageSchema>;
export type CompanyHiringJob = Schema.Schema.Type<typeof CompanyHiringJobSchema>;
export type CompanyGroupHiringSignal = Schema.Schema.Type<typeof CompanyGroupHiringSignalSchema>;
export type CurrentOpenCareerAd = Schema.Schema.Type<typeof CurrentOpenCareerAdSchema>;
export type CurrentOpenCareerAdsPage = Schema.Schema.Type<typeof CurrentOpenCareerAdsPageSchema>;
export type CurrentOpenCareerKeywordSummary = Schema.Schema.Type<
  typeof CurrentOpenCareerKeywordSummarySchema
>;
export type CurrentOpenCareerCollection = Schema.Schema.Type<
  typeof CurrentOpenCareerCollectionSchema
>;
export type CurrentOpenCareerSignal = Schema.Schema.Type<typeof CurrentOpenCareerSignalSchema>;
export type CurrentOpenCareer = Schema.Schema.Type<typeof CurrentOpenCareerSchema>;

export const CompanyHiringUrlParamsSchema = Schema.Struct({
  view: Schema.optional(Schema.Literals(["open", "observation"])),
  snapshotId: Schema.optional(UUID),
  groupSnapshotId: Schema.optional(UUID),
  offset: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    describeIntRange({ minimum: 0 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  groupOffset: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    describeIntRange({ minimum: 0 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  limit: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0)),
    Schema.check(Schema.isLessThanOrEqualTo(100)),
    describeIntRange({ minimum: 1, maximum: 100 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 50)),
    Schema.withConstructorDefault(Effect.sync(() => 50))
  ),
});

const orgnrParam = Schema.String;

const getCompanyHiringEndpoint = HttpApiEndpoint.get(
  "getCompanyHiring",
  "/careers/companies/:orgnr",
  {
    params: { orgnr: orgnrParam },
    query: CompanyHiringUrlParamsSchema,
    success: CompanyHiringResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.InternalServerErrorNoContent,
    ],
  }
);

export const CareersV3Group = HttpApiGroup.make("careersV3")
  .add(getCompanyHiringEndpoint)
  .add(
    HttpApiEndpoint.get("getCompanyCareerActivation", "/careers/companies/:orgnr/activation", {
      params: Schema.Struct({ orgnr: Schema.String }),
      success: CareerActivationResponseSchema,
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.put("setCompanyCareerActivation", "/careers/companies/:orgnr/activation", {
      params: Schema.Struct({ orgnr: Schema.String }),
      payload: CareerActivationRequestSchema,
      success: CareerActivationResponseSchema,
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.get("previewCompanyDomain", "/careers/companies/:orgnr/domains/preview", {
      params: Schema.Struct({ orgnr: Schema.String }),
      query: CompanyDomainPreviewQuerySchema,
      success: CompanyDomainPreviewResponseSchema,
      error: [
        CompanyDomainInvalidError,
        CompanyDomainNotFoundError,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.post("linkCompanyDomain", "/careers/companies/:orgnr/domains", {
      params: Schema.Struct({ orgnr: Schema.String }),
      payload: LinkCompanyDomainRequestSchema,
      success: CareerActivationResponseSchema,
      error: [
        CompanyDomainInvalidError,
        CompanyDomainNotFoundError,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.put(
      "setCompanyDomainVerification",
      "/careers/companies/:orgnr/domains/:domain/verification",
      {
        params: CompanyDomainParamsSchema,
        payload: CompanyDomainVerificationRequestSchema,
        success: CareerActivationResponseSchema,
        error: [
          CompanyDomainInvalidError,
          CompanyDomainNotFoundError,
          HttpApiError.InternalServerErrorNoContent,
        ],
      }
    )
  )
  .add(
    HttpApiEndpoint.delete("removeCompanyDomain", "/careers/companies/:orgnr/domains/:domain", {
      params: CompanyDomainParamsSchema,
      success: CareerActivationResponseSchema,
      error: [
        CompanyDomainInvalidError,
        CompanyDomainNotFoundError,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.get("getHiringTrends", "/careers/trends", {
      query: HiringTrendsQuery,
      success: HiringTrendsResponse,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get("getHiringTrendCompanies", "/careers/trends/companies", {
      query: HiringTrendCompaniesQuery,
      success: HiringTrendCompaniesResponse,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get("getHiringTrendAds", "/careers/trends/ads", {
      query: HiringTrendAdsQuery,
      success: HiringTrendAdsResponse,
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.post("getJobAdStatuses", "/cli/careers/ads/statuses", {
      payload: JobAdStatusesRequestSchema,
      success: JobAdStatusesResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.post("paginateCompanyAds", "/cli/careers/companies/ads", {
      payload: CompanyAdsPaginationRequestSchema,
      success: CompanyAdsPaginationResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.post("lookupCareersByDomains", "/cli/careers/domains/lookup", {
      payload: CareerDomainLookupRequestSchema,
      success: CareerDomainLookupResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get("getCareersByDomain", "/careers/domains/:domain", {
      params: Schema.Struct({ domain: Schema.String }),
      success: DomainCareersResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get("getCareerAdminDomains", "/careers/admin/domains", {
      success: CareerAdminDomainsResponseSchema,
      error: HttpApiError.InternalServerErrorNoContent,
    })
  )
  .add(
    HttpApiEndpoint.patch("updateCareerDomainControl", "/careers/admin/domains/:domain/control", {
      params: Schema.Struct({ domain: Schema.String }),
      payload: CareerDomainControlRequestSchema,
      success: Schema.Struct({ updated: Schema.Boolean }),
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.ForbiddenNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.post("retryCareerDomain", "/careers/admin/domains/:domain/retry", {
      params: Schema.Struct({ domain: Schema.String }),
      success: Schema.Struct({ queued: Schema.Boolean, dueAt: NullableTimestamp }),
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.ForbiddenNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.post("seedCareerDomain", "/careers/admin/domains/:domain/seed", {
      params: Schema.Struct({ domain: Schema.String }),
      payload: CareerSeedRequestSchema,
      success: Schema.Struct({ queued: Schema.Boolean, dueAt: NullableTimestamp }),
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.ForbiddenNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  );
